import React from 'react';
import { Star, Plus, X, TrendingUp, TrendingDown } from 'lucide-react';
import { PlayerCardFace } from '../../components/PlayerCard';

/**
 * Fantasy card tile — renders the OFFICIAL PlayerCardFace (same art as /estatisticas)
 * scaled down via CSS transform to fit the pitch slot or the picker modal cell.
 *
 * The natural design size is 380x507 (aspect 3/4). We scale it to fit `size` prop.
 * Overlays (captain star, +1.0 bonus badge, remove button) are placed OUTSIDE the
 * scaled inner container so they stay at their designed 1:1 pixel size.
 */

// natural card design box
const NATURAL_W = 380;
const NATURAL_H = 507;

// Preset sizes
const SIZES = {
  pitch: { w: 120, h: 160 },   // slot on tactical pitch (bigger for readability)
  picker: { w: 150, h: 200 },  // grid cell in modal
  large: { w: 220, h: 293 },
};

const FantasyCardTile = ({
  player,           // enriched profile from /api/fantasy/players
  cardData,         // full card data from /api/players/{name}/card
  slotPos,          // GK/DEF/ATA — which slot this tile is currently in
  captain,          // bool
  selected,         // bool — outline glow
  disabled,         // bool — dim/grey
  size = 'picker',  // 'pitch' | 'picker'
  onClick,
  onRemove,
}) => {
  if (!player) return null;
  const dims = SIZES[size] || SIZES.picker;
  const scale = dims.w / NATURAL_W;
  const bonus = slotPos && slotPos === player.preferred_position;

  const ready = !!cardData;

  return (
    <div
      className="relative inline-block"
      style={{ width: dims.w, height: dims.h + (size === 'pitch' ? 18 : 22) }}
      data-testid={`card-${player.player_name}`}
    >
      {/* Scaled clickable card (uses the SITE'S official face) */}
      <button
        onClick={onClick}
        disabled={disabled && !selected}
        className={`absolute top-0 left-0 rounded-[7px] overflow-hidden transition-transform outline-none
          ${selected ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-500/40 scale-[1.02]' : ''}
          ${disabled && !selected ? 'opacity-40 cursor-not-allowed grayscale' : 'hover:scale-[1.04]'}
        `}
        style={{ width: dims.w, height: dims.h }}
        aria-label={player.player_name}
      >
        {/* Inner container at natural size, scaled to fit */}
        <div
          className="pointer-events-none"
          style={{
            width: NATURAL_W,
            height: NATURAL_H,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <div className="relative w-full h-full">
            {ready ? (
              <PlayerCardFace data={cardData} />
            ) : (
              // Skeleton while card data loads
              <div className="absolute inset-0 rounded-[28px] bg-slate-800 animate-pulse flex items-center justify-center">
                <div className="text-slate-500 text-3xl font-black">{player.player_name.charAt(0)}</div>
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Price ribbon — OUTSIDE the scaled area so it stays sharp and never covers card art */}
      <div className={`absolute left-0 right-0 bg-emerald-500 text-slate-900 font-black text-center rounded-b-md flex items-center justify-center gap-1 ${size === 'pitch' ? 'text-[10px] py-0.5' : 'text-xs py-1'}`}
           style={{ top: dims.h + 2 }}>
        {player.current_price?.toFixed(1)} €M
        {(() => {
          if (player.previous_price == null || player.previous_price === player.current_price) return null;
          const delta = player.current_price - player.previous_price;
          const up = delta > 0;
          const Icon = up ? TrendingUp : TrendingDown;
          return (
            <span className={`inline-flex items-center gap-0.5 rounded px-1 ${up ? 'bg-white/30' : 'bg-red-500/50 text-white'}`}>
              <Icon className="w-3 h-3" />
              {up ? '+' : ''}{delta.toFixed(1)}
            </span>
          );
        })()}
      </div>

      {/* Captain star (overlay, outside scale) */}
      {captain && (
        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 border-2 border-amber-500 flex items-center justify-center shadow-lg z-20">
          <Star className="w-3.5 h-3.5 text-slate-900 fill-slate-900" />
        </div>
      )}

      {/* Bonus +1.0 badge */}
      {bonus && selected && (
        <div className={`absolute ${captain ? 'top-7' : '-top-1'} -right-1 bg-emerald-500 text-slate-900 text-[9px] font-black px-1.5 py-0.5 rounded shadow-md z-20`}
             data-testid={`bonus-${player.player_name}`}>
          +1.0
        </div>
      )}

      {/* Remove button */}
      {selected && onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-400 text-white flex items-center justify-center shadow-md z-30"
          data-testid={`remove-${player.player_name}`}
          aria-label="Remover"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

/** Placeholder for an empty slot on the pitch. */
export const EmptySlot = ({ label, onClick, size = 'pitch' }) => {
  const dims = SIZES[size] || SIZES.pitch;
  return (
    <button
      onClick={onClick}
      className="relative rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border-2 border-dashed border-emerald-400/40 hover:border-emerald-400 transition-colors flex flex-col items-center justify-center"
      style={{ width: dims.w, height: dims.h }}
      data-testid={`empty-slot-${label}`}
    >
      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center mb-1">
        <Plus className="w-5 h-5 text-emerald-300" />
      </div>
      <div className="text-emerald-300 text-[10px] font-black tracking-widest">{label}</div>
    </button>
  );
};

export default FantasyCardTile;
